const SCALE_CONFIGS = {
    void: {
        label: 'Camera Z', min: 5, max: 50, step: 0.5, default: 25,
        configKey: null,
        apply(val, preset)   { if (preset?._camera) preset._camera.position.z = parseFloat(val) },
        restore(val, preset) { if (preset?._camera) preset._camera.position.z = val }
    },
    bloom: {
        label: 'Sphere Size', min: 0.5, max: 2.0, step: 0.05, default: 1.0,
        configKey: 'sphereSize',
        apply(val, preset)   { preset?.setConfig({ sphereSize: parseFloat(val) }) },
        restore(val, preset) { preset?.setConfig({ sphereSize: val }) }
    },
    neon: {
        label: 'Scale', min: 0.5, max: 2.0, step: 0.05, default: 1.0,
        configKey: 'scale',
        apply(val, preset)   { preset?.setConfig({ scale: parseFloat(val) }) },
        restore(val, preset) { preset?.setConfig({ scale: val }) }
    },
    organic: {
        label: 'Scale', min: 0.5, max: 2.0, step: 0.05, default: 1.0,
        configKey: 'scale',
        apply(val, preset)   { preset?.setConfig({ scale: parseFloat(val) }) },
        restore(val, preset) { preset?.setConfig({ scale: val }) }
    },
    minimal: {
        label: 'Scale', min: 0.5, max: 2.0, step: 0.05, default: 1.0,
        configKey: 'scale',
        apply(val, preset)   { preset?.setConfig({ scale: parseFloat(val) }) },
        restore(val, preset) { preset?.setConfig({ scale: val }) }
    },
    particles: {
        label: 'Camera Z', min: 1.0, max: 6.0, step: 0.1, default: 2.5,
        configKey: null,
        apply(val, preset)   { if (preset?._camera) preset._camera.position.z = parseFloat(val) },
        restore(val, preset) { if (preset?._camera) preset._camera.position.z = val }
    }
}

export default class ExportPreviewOverlay {
    constructor({ onRecord, onStop, onDeactivate }) {
        this._onRecord        = onRecord
        this._onStop          = onStop
        this._onDeactivate    = onDeactivate
        this._active          = false
        this.activeFormat     = null
        this._livePreset      = null
        this._scaleCfg        = null
        this._origScaleVal    = null
        this._currentScaleVal = null
        this._controlsEl      = null
        this._recording       = false

        this._buildDOM()
    }

    activate(format, activePresetId, presetConfig, livePreset) {
        // Remove any previous preview class before applying the new one
        if (this._active) this._clearCanvasClass()

        this._active      = true
        this.activeFormat = format
        this._livePreset  = livePreset
        this._scaleCfg    = SCALE_CONFIGS[activePresetId] ?? null

        const cfg = this._scaleCfg
        if (cfg) {
            this._origScaleVal = cfg.configKey
                ? parseFloat(presetConfig[cfg.configKey] ?? cfg.default)
                : (livePreset?._camera?.position.z ?? cfg.default)
            this._currentScaleVal = this._origScaleVal
        } else {
            this._origScaleVal    = null
            this._currentScaleVal = null
        }

        document.getElementById('canvasArea')?.classList.add(`preview-mode-${format}`)
        this._updateSidebarControl(activePresetId, presetConfig, livePreset)
        this._controlsEl.classList.remove('hidden')
    }

    deactivate() {
        if (!this._active) return
        this._clearCanvasClass()
        this._active      = false
        this.activeFormat = null
        this._recording = false
        this._controlsEl.querySelector('.export-preview-record-btn').textContent = '⏺ Record'
        this._controlsEl.classList.add('hidden')

        if (this._scaleCfg && this._origScaleVal !== null) {
            this._scaleCfg.restore(this._origScaleVal, this._livePreset)
        }
        this._livePreset      = null
        this._scaleCfg        = null
        this._origScaleVal    = null
        this._currentScaleVal = null

        this._onDeactivate?.()
    }

    isActive() { return this._active }

    getScaleConfig() {
        const cfg = this._scaleCfg
        if (!cfg || this._currentScaleVal === null) return null
        return cfg.configKey
            ? { [cfg.configKey]: this._currentScaleVal }
            : { cameraZ: this._currentScaleVal }
    }

    // ── Private ──────────────────────────────────────────────────────────────

    _clearCanvasClass() {
        const area = document.getElementById('canvasArea')
        if (!area) return
        area.classList.remove('preview-mode-9x16', 'preview-mode-16x9', 'preview-mode-1x1')
    }

    _buildDOM() {
        this._controlsEl = document.createElement('div')
        this._controlsEl.id = 'exportPreviewControls'
        this._controlsEl.className = 'hidden'
        this._controlsEl.innerHTML = `
<div class="export-preview-ctrl-header">
    <span class="export-preview-ctrl-title"></span>
    <button class="export-preview-cancel-btn">✕</button>
</div>
<div class="export-preview-ctrl-scale hidden">
    <label class="export-preview-ctrl-label">
        <span class="ctrl-name"></span>
        <span class="ctrl-val"></span>
    </label>
    <input type="range" class="export-preview-ctrl-slider">
</div>
<button class="export-preview-record-btn">⏺ Record</button>`

        this._controlsEl.querySelector('.export-preview-cancel-btn')
            .addEventListener('click', () => this.deactivate())

        this._controlsEl.querySelector('.export-preview-record-btn')
            .addEventListener('click', () => {
                if (this._recording) {
                    this._onStop?.()
                } else {
                    this._onRecordClick()
                }
            })

        // Insert before export history so Record appears above the history list
        const exportHistory = document.getElementById('exportHistory')
        const exportSection = document.getElementById('exportSection')
        if (exportHistory) {
            exportHistory.parentNode.insertBefore(this._controlsEl, exportHistory)
        } else if (exportSection) {
            exportSection.appendChild(this._controlsEl)
        } else {
            document.body.appendChild(this._controlsEl)
        }
    }

    _onRecordClick() {
        const format      = this.activeFormat
        const scaleConfig = this.getScaleConfig()

        // Slider already applied the desired camera Z / scale to the live preset.
        // Pass scaleConfig so _startRecording saves the current value as the restore
        // point; deactivate() will restore to origScaleVal after stop.
        this._recording = true
        this._controlsEl.querySelector('.export-preview-record-btn').textContent = '⏹ Stop'

        this._onRecord(format, scaleConfig)
    }

    _updateSidebarControl(presetId, presetConfig, livePreset) {
        const titleEl    = this._controlsEl.querySelector('.export-preview-ctrl-title')
        const scaleBlock = this._controlsEl.querySelector('.export-preview-ctrl-scale')
        const nameEl     = this._controlsEl.querySelector('.ctrl-name')
        const valEl      = this._controlsEl.querySelector('.ctrl-val')
        const slider     = this._controlsEl.querySelector('.export-preview-ctrl-slider')
        const cfg        = this._scaleCfg

        titleEl.textContent = this.activeFormat.replace('x', ':')

        if (!cfg) {
            scaleBlock.classList.add('hidden')
            return
        }

        const initVal = this._origScaleVal
        nameEl.textContent = cfg.label
        valEl.textContent  = initVal.toFixed(2)
        slider.min   = cfg.min
        slider.max   = cfg.max
        slider.step  = cfg.step
        slider.value = initVal
        scaleBlock.classList.remove('hidden')

        slider.oninput = () => {
            const v = parseFloat(slider.value)
            this._currentScaleVal = v
            valEl.textContent = v.toFixed(2)
            cfg.apply(v, livePreset)
        }
    }
}
